import React, { useEffect, useState } from "react";
import { ITicket } from "../models/ITicket";
import { getAllDepartments, getAllTicketSubjects, updateTicket } from "../services/ticketService";

interface Department { id: string; name: string; description: string; }
interface Subject    { id: string; name: string; description: string; }

interface Props {
  isOpen: boolean;
  onClose: () => void;
  onUpdated: () => void;
  ticket: ITicket;
}

export default function EditTicketModal({ isOpen, onClose, onUpdated, ticket }: Props) {
  const [departments, setDepartments] = useState<Department[]>([]);
  const [subjects, setSubjects]       = useState<Subject[]>([]);
  const [title, setTitle]             = useState(ticket.title);
  const [note, setNote]               = useState(ticket.note ?? "");
  const [departmentId, setDepartmentId] = useState(ticket.ticketDepartmentId);
  const [subjectId, setSubjectId]     = useState(ticket.ticketSubjectId ?? "");
  const [saving, setSaving]           = useState(false);
  const [error, setError]             = useState("");

  const hasMessages = ticket.messageCount > 0;

  const origTitle   = ticket.title;
  const origNote    = ticket.note ?? "";
  const origDept    = ticket.ticketDepartmentId;
  const origSubject = ticket.ticketSubjectId ?? "";

  const isDirty =
    title.trim() !== origTitle ||
    note.trim()  !== origNote  ||
    departmentId !== origDept  ||
    subjectId    !== origSubject;

  useEffect(() => {
    if (!isOpen) return;
    setTitle(ticket.title);
    setNote(ticket.note ?? "");
    setDepartmentId(ticket.ticketDepartmentId);
    setSubjectId(ticket.ticketSubjectId ?? "");
    setError("");
    getAllDepartments()
      .then((res) => setDepartments(res.data ?? []))
      .catch(() => {});
    getAllTicketSubjects()
      .then((res) => setSubjects(res.data ?? []))
      .catch(() => {});
  }, [isOpen]);

  const handleSubmit = async () => {
    if (!title.trim()) { setError("Başlık zorunludur."); return; }
    if (!departmentId) { setError("Lütfen bir departman seçin."); return; }
    setSaving(true);
    setError("");
    try {
      await updateTicket({
        ticketId: ticket.id,
        title: title.trim(),
        note: note.trim(),
        ticketDepartmentId: departmentId,
        ...(subjectId ? { ticketSubjectId: subjectId } : {}),
      });
      onClose();
      onUpdated();
    } catch {
      setError("Ticket güncellenemedi. Lütfen tekrar deneyin.");
    } finally {
      setSaving(false);
    }
  };

  if (!isOpen) return null;

  const readonlyStyle = {
    width: "100%", padding: "10px 14px", boxSizing: "border-box" as const,
    border: "1.5px solid #e5e7eb", borderRadius: "10px",
    fontSize: "0.9rem", color: "#9ca3af",
    background: "#f9fafb", cursor: "not-allowed",
  };

  const editStyle = {
    width: "100%", padding: "10px 14px", boxSizing: "border-box" as const,
    border: "1.5px solid #e5e7eb", borderRadius: "10px",
    fontSize: "0.9rem", outline: "none", color: "#111827",
  };

  const selectStyle = {
    width: "100%", padding: "10px 14px",
    border: "1.5px solid #e5e7eb", borderRadius: "10px",
    fontSize: "0.9rem", outline: "none", color: "#374151",
    background: "#fafafa", cursor: "pointer",
  };

  return (
    <div
      onClick={onClose}
      style={{
        position: "fixed", inset: 0,
        background: "rgba(0,0,0,0.55)",
        zIndex: 9999,
        display: "flex", alignItems: "center", justifyContent: "center",
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          background: "#fff", borderRadius: "20px",
          width: "92%", maxWidth: "520px",
          boxShadow: "0 12px 48px rgba(0,0,0,0.22)",
          overflow: "hidden",
        }}
      >
        {/* Header */}
        <div style={{
          padding: "20px 24px 18px",
          background: "linear-gradient(135deg, #0891b2, #0e7490)",
          display: "flex", alignItems: "center", justifyContent: "space-between",
        }}>
          <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
            <div style={{
              width: "36px", height: "36px", borderRadius: "10px",
              background: "rgba(255,255,255,0.2)",
              display: "flex", alignItems: "center", justifyContent: "center",
            }}>
              <i className="bi bi-pencil-square text-white" style={{ fontSize: "1.1rem" }}></i>
            </div>
            <div>
              <div style={{ fontWeight: 800, fontSize: "1rem", color: "#fff" }}>Ticket Güncelle</div>
              <div style={{ fontSize: "0.75rem", color: "rgba(255,255,255,0.75)" }}>
                {hasMessages ? "Mesaj olduğu için sadece departman ve konu değiştirilebilir" : "Tüm alanlar düzenlenebilir"}
              </div>
            </div>
          </div>
          <button
            onClick={onClose}
            style={{
              background: "rgba(255,255,255,0.15)", border: "none",
              borderRadius: "8px", width: "32px", height: "32px",
              display: "flex", alignItems: "center", justifyContent: "center",
              cursor: "pointer", color: "#fff", fontSize: "1.1rem",
            }}
          >×</button>
        </div>

        {/* Body */}
        <div style={{ padding: "24px" }}>
          {error && (
            <div style={{
              marginBottom: "16px", padding: "9px 14px", borderRadius: "10px",
              background: "#fef2f2", border: "1px solid #fecaca",
              color: "#dc2626", fontSize: "0.83rem", display: "flex", alignItems: "center", gap: "8px",
            }}>
              <i className="bi bi-exclamation-circle-fill"></i>{error}
            </div>
          )}

          {hasMessages && (
            <div style={{
              marginBottom: "16px", padding: "9px 14px", borderRadius: "10px",
              background: "#fffbeb", border: "1px solid #fde68a",
              color: "#92400e", fontSize: "0.82rem", display: "flex", alignItems: "center", gap: "8px",
            }}>
              <i className="bi bi-lock-fill" style={{ color: "#f59e0b" }}></i>
              Bu tickette mesaj olduğu için başlık ve açıklama düzenlenemez.
            </div>
          )}

          {/* Konu */}
          {subjects.length > 0 && (
            <div style={{ marginBottom: "16px" }}>
              <label style={{ display: "block", fontWeight: 600, fontSize: "0.82rem", color: "#374151", marginBottom: "6px" }}>
                Konu
              </label>
              <select
                value={subjectId}
                onChange={(e) => setSubjectId(e.target.value)}
                style={{ ...selectStyle, color: subjectId ? "#374151" : "#9ca3af" }}
              >
                <option value="">— Konu seçin —</option>
                {subjects.map((s) => (
                  <option key={s.id} value={s.id}>{s.name}</option>
                ))}
              </select>
            </div>
          )}

          {/* Başlık */}
          <div style={{ marginBottom: "16px" }}>
            <label style={{ display: "flex", alignItems: "center", gap: "6px", fontWeight: 600, fontSize: "0.82rem", color: "#374151", marginBottom: "6px" }}>
              Başlık <span style={{ color: "#dc2626" }}>*</span>
              {hasMessages && <i className="bi bi-lock-fill" style={{ fontSize: "0.7rem", color: "#d97706" }}></i>}
            </label>
            <input
              type="text"
              value={title}
              onChange={(e) => !hasMessages && setTitle(e.target.value)}
              readOnly={hasMessages}
              maxLength={120}
              style={hasMessages ? readonlyStyle : editStyle}
            />
          </div>

          {/* Not */}
          <div style={{ marginBottom: "16px" }}>
            <label style={{ display: "flex", alignItems: "center", gap: "6px", fontWeight: 600, fontSize: "0.82rem", color: "#374151", marginBottom: "6px" }}>
              Açıklama / Not
              {hasMessages && <i className="bi bi-lock-fill" style={{ fontSize: "0.7rem", color: "#d97706" }}></i>}
            </label>
            <textarea
              rows={3}
              value={note}
              onChange={(e) => !hasMessages && setNote(e.target.value)}
              readOnly={hasMessages}
              style={{
                ...(hasMessages ? readonlyStyle : editStyle),
                resize: "vertical", fontFamily: "inherit",
              }}
            />
          </div>

          {/* Departman */}
          <div style={{ marginBottom: "16px" }}>
            <label style={{ display: "block", fontWeight: 600, fontSize: "0.82rem", color: "#374151", marginBottom: "6px" }}>
              Departman <span style={{ color: "#dc2626" }}>*</span>
            </label>
            <select
              value={departmentId}
              onChange={(e) => setDepartmentId(e.target.value)}
              style={selectStyle}
            >
              {departments.map((d) => (
                <option key={d.id} value={d.id}>{d.name}</option>
              ))}
            </select>
          </div>

          {/* Footer */}
          <div style={{ display: "flex", gap: "10px", justifyContent: "flex-end" }}>
            <button
              onClick={onClose}
              disabled={saving}
              style={{
                padding: "10px 20px", borderRadius: "10px",
                border: "1.5px solid #e5e7eb", background: "#fff",
                color: "#374151", fontWeight: 600, fontSize: "0.88rem", cursor: "pointer",
              }}
            >
              İptal
            </button>
            <button
              onClick={handleSubmit}
              disabled={saving || !isDirty}
              style={{
                padding: "10px 24px", borderRadius: "10px", border: "none",
                background: !saving && isDirty ? "linear-gradient(135deg, #0891b2, #0e7490)" : "#e5e7eb",
                color: !saving && isDirty ? "#fff" : "#9ca3af",
                fontWeight: 700, fontSize: "0.88rem",
                cursor: !saving && isDirty ? "pointer" : "not-allowed",
                display: "flex", alignItems: "center", gap: "8px",
                transition: "opacity 0.15s",
              }}
            >
              {saving
                ? <><span className="spinner-border spinner-border-sm" role="status" /> Güncelleniyor...</>
                : <><i className="bi bi-check2-circle"></i> Ticket Güncelle</>
              }
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
